import React, { useState, useEffect } from 'react';
import { 
  FaUpload, 
  FaTrash, 
  FaBullhorn, 
  FaImages, 
  FaRss,
  FaChartLine,
  FaBuilding,
  FaDollarSign,
  FaNewspaper,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaClock
} from 'react-icons/fa';
import { MdFeed, MdNotifications } from 'react-icons/md';
import { IoSparkles } from 'react-icons/io5';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { useAuth } from '../security/security/AuthContext';

const AdminAddFeed = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { userDetails, token } = useAuth();
  
  const [formData, setFormData] = useState({
    description: '',
    feedType: 'GENERAL',
    active: true,
  });

  const feedTypes = [
    { value: 'GENERAL', label: 'General Update', icon: <MdFeed />, color: '#6366f1' },
    { value: 'EARNINGS_UPDATE', label: 'Earnings Update', icon: <FaDollarSign />, color: '#10b981' },
    { value: 'NEW_COMPANY', label: 'New Company', icon: <FaBuilding />, color: '#f59e0b' },
    { value: 'PROFIT_ANNOUNCEMENT', label: 'Profit News', icon: <FaChartLine />, color: '#8b5cf6' },
    { value: 'MARKET_UPDATE', label: 'Market Update', icon: <FaClock />, color: '#ef4444' }
  ];

  useEffect(() => {
    fetchFeeds();
  }, []);

  const fetchFeeds = async () => {
   try {
     setLoading(true);
     const response = await axios.get('https://api.globallequityfinance.com/api/admin/feeds', {
       headers: { 'Authorization': token }
     });
     setFeeds(response.data);
   } catch (error) {
     console.error('Error fetching feeds:', error);
     toast.error('Failed to fetch feeds');
   } finally {
     setLoading(false);
   }
 };

 const handleInputChange = (e) => {
   const { name, value, type, checked } = e.target;
   setFormData({
     ...formData,
     [name]: type === 'checkbox' ? checked : value
   });
 };

 const handleImageUpload = (e) => {
   const file = e.target.files[0];
   if (file) {
     if (file.size > 5 * 1024 * 1024) {
       toast.error('Image size should be less than 5MB');
       return;
     }
     setSelectedImage(file);
   }
 };

 const removeImage = () => {
   setSelectedImage(null);
   const fileInput = document.getElementById('feed-image');
   if (fileInput) fileInput.value = '';
 };

 const handleSubmit = async (e) => {
   e.preventDefault();
   
   if (!formData.description.trim()) {
     toast.error('Please enter a description');
     return;
   }

   setSubmitLoading(true);
   const submitFormData = new FormData();
   submitFormData.append('description', formData.description);
   submitFormData.append('feedType', formData.feedType);
   submitFormData.append('active', formData.active);
   
   if (selectedImage) {
     submitFormData.append('image', selectedImage);
   }

   try {
     await axios.post('https://api.globallequityfinance.com/api/admin/feeds', submitFormData, {
       headers: {
         'Authorization': token,
         'Content-Type': 'multipart/form-data',
       },
     });

     toast.success('Feed posted successfully!');
     setFormData({
       description: '',
       feedType: 'GENERAL',
       active: true,
     });
     setSelectedImage(null);
     const fileInput = document.getElementById('feed-image');
     if (fileInput) fileInput.value = '';
     fetchFeeds();
   } catch (error) {
     console.error('Error creating feed:', error);
     toast.error('Failed to post feed. Please try again.');
   } finally {
     setSubmitLoading(false);
   }
 };

 const deleteFeed = async (id) => {
   if (!window.confirm('Are you sure you want to delete this feed?')) return;
   
   try {
     await axios.delete(`https://api.globallequityfinance.com/api/admin/feeds/${id}`, {
       headers: { 'Authorization': token }
     });
     toast.success('Feed deleted successfully');
     fetchFeeds();
   } catch (error) {
     console.error('Error deleting feed:', error);
     toast.error('Failed to delete feed');
   }
 };

 const toggleFeedStatus = async (id) => {
   try {
     await axios.put(`https://api.globallequityfinance.com/api/admin/feeds/${id}/toggle-status`, {}, {
       headers: { 'Authorization': token }
     });
     toast.success('Feed status updated');
     fetchFeeds();
   } catch (error) {
     console.error('Error updating feed status:', error);
     toast.error('Failed to update feed status');
   }
 };

 const formatDate = (dateString) => {
   return new Date(dateString).toLocaleDateString('en-US', {
     year: 'numeric',
     month: 'short',
     day: 'numeric',
     hour: '2-digit',
     minute: '2-digit'
   });
 };

 return (
   <div className="admin-feed-container">
     <div className="admin-feed-header">
       <div className="header-glow"></div>
       <div className="header-content">
         <div className="header-title-section">
           <div className="title-icon-wrapper">
             <FaBullhorn className="title-icon" />
             <IoSparkles className="sparkle sparkle-1" />
             <IoSparkles className="sparkle sparkle-2" />
           </div>
           <div className="title-text">
             <h1 className="main-title">Manage Feeds</h1>
             <p className="subtitle">Share updates and engage with your community</p>
           </div>
         </div>
       </div>
     </div>

     <div className="admin-feed-content">
       {/* Create Feed Form */}
       <div className="create-feed-section">
         <div className="section-header">
           <h2 className="section-title">
             <MdNotifications className="section-icon" />
             Create New Feed
           </h2>
         </div>

         <form onSubmit={handleSubmit} className="feed-form">
           <div className="form-grid">
             <div className="form-group full-width">
               <label className="form-label">
                 <FaNewspaper className="label-icon" />
                 Feed Type
               </label>
               <div className="feed-type-grid">
                 {feedTypes.map((type) => (
                   <label
                     key={type.value}
                     className={`feed-type-option ${formData.feedType === type.value ? 'selected' : ''}`}
                     style={{ '--type-color': type.color }}
                   >
                     <input
                       type="radio"
                       name="feedType"
                       value={type.value}
                       checked={formData.feedType === type.value}
                       onChange={handleInputChange}
                       className="type-radio"
                     />
                     <div className="type-content">
                       <div className="type-icon" style={{ color: type.color }}>
                         {type.icon}
                       </div>
                       <span className="type-label">{type.label}</span>
                     </div>
                   </label>
                 ))}
               </div>
             </div>

             <div className="form-group full-width">
               <label className="form-label">
                 <FaRss className="label-icon" />
                 Description
               </label>
               <div className="textarea-wrapper">
                 <textarea
                   name="description"
                   value={formData.description}
                   onChange={handleInputChange}
                   placeholder="Share your update with the community..."
                   className="feed-textarea"
                   rows="4"
                   required
                 />
               </div>
             </div>

             <div className="form-group">
               <label className="form-label">
                 <FaImages className="label-icon" />
                 Upload Image (Optional)
               </label>
               
               <input
                 type="file"
                 id="feed-image"
                 accept="image/*"
                 onChange={handleImageUpload}
                 className="file-input"
               />
               
               {selectedImage ? (
                 <div className="image-preview">
                   <img 
                     src={URL.createObjectURL(selectedImage)} 
                     alt="Preview" 
                     className="preview-image"
                   />
                   <button 
                     type="button" 
                     className="remove-image-btn"
                     onClick={removeImage}
                   >
                     <FaTimes />
                   </button>
                 </div>
               ) : (
                 <label htmlFor="feed-image" className="upload-area">
                   <div className="upload-content">
                     <FaUpload className="upload-icon" />
                     <span className="upload-text">Click to upload image</span>
                     <span className="upload-hint">PNG, JPG up to 5MB</span>
                   </div>
                 </label>
               )}
             </div>

             <div className="form-group">
               <label className="checkbox-container">
                 <input
                   type="checkbox"
                   name="active"
                   checked={formData.active}
                   onChange={handleInputChange}
                   className="checkbox-input"
                 />
                 <span className="checkbox-checkmark"></span>
                 <span className="checkbox-label">Publish immediately</span>
               </label>
             </div>
           </div>

           <div className="form-actions">
             <button 
               type="submit" 
               className="submit-btn"
               disabled={submitLoading}
             >
               <div className="btn-content">
                 {submitLoading ? (
                   <>
                     <FaSpinner className="btn-icon spinning" />
                     <span>Posting...</span>
                   </>
                 ) : (
                   <>
                     <FaBullhorn className="btn-icon" />
                     <span>Post Feed</span>
                   </>
                 )}
               </div>
               <div className="btn-shine"></div>
             </button>
           </div>
         </form>
       </div>

       {/* Existing Feeds */}
       <div className="feeds-list-section">
         <div className="section-header">
           <h2 className="section-title">
             <FaRss className="section-icon" />
             Recent Feeds ({feeds.length})
           </h2>
         </div>

         {loading ? (
           <div className="loading-state">
             <FaSpinner className="loading-spinner" />
             <span>Loading feeds...</span>
           </div>
         ) : feeds.length > 0 ? (
           <div className="feeds-grid">
             {feeds.slice(0, 6).map((feed) => {
               const feedType = feedTypes.find(t => t.value === feed.feedType);
               return (
                 <div key={feed.id} className="feed-card">
                   <div className="feed-card-header">
                     <div className="feed-type-badge" style={{ backgroundColor: feedType?.color }}>
                       {feedType?.icon}
                       <span>{feedType?.label}</span>
                     </div>
                     <div className="feed-actions">
                       <button 
                         className={`status-toggle ${feed.active ? 'active' : 'inactive'}`}
                         onClick={() => toggleFeedStatus(feed.id)}
                         title={feed.active ? 'Deactivate' : 'Activate'}
                       >
                         {feed.active ? <FaCheck /> : <FaTimes />}
                       </button>
                       <button 
                         className="delete-btn"
                         onClick={() => deleteFeed(feed.id)}
                         title="Delete feed"
                       >
                         <FaTrash />
                       </button>
                     </div>
                   </div>

                   {feed.imageUrl && (
                     <div className="feed-image-container">
                       <img 
                         src={`https://api.globallequityfinance.com${feed.imageUrl}`}
                         alt="Feed"
                         className="feed-image"
                       />
                     </div>
                   )}

                   <div className="feed-content">
                     <p className="feed-description">{feed.description}</p>
                     <div className="feed-meta">
                       <span className="feed-date">
                         <FaClock className="clock-icon" />
                         {formatDate(feed.createdAt)}
                       </span>
                       <span className={`feed-status ${feed.active ? 'active' : 'inactive'}`}>
                         {feed.active ? 'Active' : 'Inactive'}
                       </span>
                     </div>
                   </div>
                 </div>
               );
             })}
           </div>
         ) : (
           <div className="empty-state">
             <FaRss className="empty-icon" />
             <h3>No feeds yet</h3>
             <p>Create your first feed to engage with your community</p>
           </div>
         )}
       </div>
     </div>

     <ToastContainer position="top-right" autoClose={5000} />

     <style jsx>{`
       .admin-feed-container {
         --primary: #2176FF;
         --primary-light: #6CA6FF;
         --primary-dark: #0D4CB3;
         --secondary: #33A1FD;
         --accent: #FFC107;
         --success: #4CAF50;
         --danger: #F44336;
         --dark-bg: #0A1128;
         --card-bg: #0F1C3F;
         --card-bg-lighter: #192952;
         --text-color: #F0F5FF;
         --text-muted: #B0C4DE;
         --border-color: rgba(82, 119, 204, 0.2);
         
         background: var(--dark-bg);
         min-height: 100vh;
         color: var(--text-color);
         font-family: 'Inter', system-ui, sans-serif;
         padding: 20px;
       }

       .admin-feed-header {
         background: linear-gradient(135deg, var(--primary), var(--primary-dark));
         padding: 2rem;
         position: relative;
         overflow: hidden;
         border-radius: 16px;
         margin-bottom: 2rem;
       }

       .header-glow {
         position: absolute;
         top: -50%;
         left: -50%;
         width: 200%;
         height: 200%;
         background: radial-gradient(circle, rgba(33, 118, 255, 0.1), transparent 50%);
         animation: glow-rotate 20s linear infinite;
       }

       @keyframes glow-rotate {
         0% { transform: rotate(0deg); }
         100% { transform: rotate(360deg); }
       }

       .header-content {
         max-width: 1200px;
         margin: 0 auto;
         position: relative;
         z-index: 2;
       }

       .header-title-section {
         display: flex;
         align-items: center;
         gap: 1.5rem;
       }

       .title-icon-wrapper {
         position: relative;
         width: 64px;
         height: 64px;
         background: rgba(255, 255, 255, 0.1);
         border-radius: 16px;
         display: flex;
         align-items: center;
         justify-content: center;
         backdrop-filter: blur(10px);
       }

       .title-icon {
         font-size: 2rem;
         color: white;
       }

       .sparkle {
         position: absolute;
         font-size: 0.75rem;
         color: #ffd700;
         animation: sparkle-twinkle 2s infinite;
       }

       .sparkle-1 {
         top: -4px;
         right: -4px;
         animation-delay: 0.5s;
       }

       .sparkle-2 {
         bottom: -4px;
         left: -4px;
         animation-delay: 1s;
       }

       @keyframes sparkle-twinkle {
         0%, 100% { opacity: 0; transform: scale(0.5); }
         50% { opacity: 1; transform: scale(1); }
       }

       .title-text h1 {
         font-size: 2rem;
         font-weight: 800;
         margin: 0;
         color: white;
       }

       .title-text p {
         font-size: 1rem;
         color: rgba(255, 255, 255, 0.8);
         margin: 0.25rem 0 0 0;
       }

       .admin-feed-content {
         max-width: 1200px;
         margin: 0 auto;
         display: grid;
         gap: 2rem;
       }

       .create-feed-section, .feeds-list-section {
         background: var(--card-bg);
         border-radius: 16px;
         padding: 2rem;
         border: 1px solid var(--border-color);
       }

       .section-header {
         margin-bottom: 1.5rem;
       }

       .section-title {
         display: flex;
         align-items: center;
         gap: 0.75rem;
         font-size: 1.5rem;
         font-weight: 700;
         color: var(--text-color);
         margin: 0;
       }

       .section-icon {
         color: var(--primary-light);
       }

       .feed-form {
         width: 100%;
       }

       .form-grid {
         display: grid;
         gap: 1.5rem;
         grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
       }

       .form-group.full-width {
         grid-column: 1 / -1;
       }

       .form-label {
         display: flex;
         align-items: center;
         gap: 0.5rem;
         font-size: 0.875rem;
         font-weight: 600;
         color: var(--text-muted);
         margin-bottom: 0.75rem;
       }

       .label-icon {
         color: var(--primary-light);
       }

       .feed-type-grid {
         display: grid;
         grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
         gap: 1rem;
       }

       .feed-type-option {
         background: var(--card-bg-lighter);
         border: 2px solid transparent;
         border-radius: 12px;
         padding: 1rem;
         cursor: pointer;
         transition: all 0.3s ease;
         position: relative;
         overflow: hidden;
       }

       .feed-type-option:hover {
         border-color: var(--type-color);
         transform: translateY(-2px);
       }

       .feed-type-option.selected {
         border-color: var(--type-color);
         background: rgba(var(--type-color), 0.1);
         box-shadow: 0 0 20px rgba(var(--type-color), 0.3);
       }

       .type-radio {
         display: none;
       }

       .type-content {
         display: flex;
         flex-direction: column;
         align-items: center;
         gap: 0.5rem;
       }

       .type-icon {
         font-size: 1.5rem;
       }

       .type-label {
         font-size: 0.875rem;
         font-weight: 500;
         color: var(--text-color);
         text-align: center;
       }

       .textarea-wrapper {
         position: relative;
       }

       .feed-textarea {
         width: 100%;
         background: var(--card-bg-lighter);
         border: 2px solid var(--border-color);
         border-radius: 12px;
         padding: 1rem;
         color: var(--text-color);
         font-size: 1rem;
         font-family: inherit;
         resize: vertical;
         transition: border-color 0.3s ease;
       }

       .feed-textarea:focus {
         outline: none;
         border-color: var(--primary);
         box-shadow: 0 0 0 3px rgba(33, 118, 255, 0.1);
       }

       .feed-textarea::placeholder {
         color: var(--text-muted);
       }

       .file-input {
         display: none;
       }

       .upload-area {
         display: flex;
         flex-direction: column;
         align-items: center;
         justify-content: center;
         min-height: 120px;
         background: var(--card-bg-lighter);
         border: 2px dashed var(--border-color);
         border-radius: 12px;
         cursor: pointer;
         transition: all 0.3s ease;
       }

       .upload-area:hover {
         border-color: var(--primary);
         background: rgba(33, 118, 255, 0.05);
       }

       .upload-content {
         display: flex;
         flex-direction: column;
         align-items: center;
         gap: 0.5rem;
       }

       .upload-icon {
         font-size: 2rem;
         color: var(--primary-light);
       }

       .upload-text {
         font-weight: 600;
         color: var(--text-color);
       }

       .upload-hint {
         font-size: 0.875rem;
         color: var(--text-muted);
       }

       .image-preview {
         position: relative;
         display: inline-block;
         border-radius: 12px;
         overflow: hidden;
       }

       .preview-image {
         width: 200px;
         height: 120px;
         object-fit: cover;
         border-radius: 12px;
       }

       .remove-image-btn {
         position: absolute;
         top: 8px;
         right: 8px;
         background: var(--danger);
         color: white;
         border: none;
         border-radius: 50%;
         width: 32px;
         height: 32px;
         display: flex;
         align-items: center;
         justify-content: center;
         cursor: pointer;
         transition: transform 0.2s ease;
       }

       .remove-image-btn:hover {
         transform: scale(1.1);
       }

       .checkbox-container {
         display: flex;
         align-items: center;
         gap: 0.75rem;
         cursor: pointer;
       }

       .checkbox-input {
         display: none;
       }

       .checkbox-checkmark {
         width: 20px;
         height: 20px;
         border: 2px solid var(--border-color);
         border-radius: 4px;
         position: relative;
         transition: all 0.3s ease;
       }

       .checkbox-input:checked + .checkbox-checkmark {
         background: var(--primary);
         border-color: var(--primary);
       }

       .checkbox-input:checked + .checkbox-checkmark::after {
         content: '✓';
         position: absolute;
         top: 50%;
         left: 50%;
         transform: translate(-50%, -50%);
         color: white;
         font-size: 0.75rem;
         font-weight: bold;
       }

       .checkbox-label {
         font-size: 0.875rem;
         color: var(--text-color);
       }

       .form-actions {
         grid-column: 1 / -1;
         display: flex;
         justify-content: center;
         margin-top: 1rem;
       }

       .submit-btn {
         position: relative;
         background: linear-gradient(135deg, var(--accent), #FF9800);
         color: #212121;
         border: none;
         border-radius: 12px;
         padding: 1rem 2rem;
         font-size: 1rem;
         font-weight: 600;
         cursor: pointer;
         transition: all 0.3s ease;
         overflow: hidden;
         min-width: 200px;
       }

       .submit-btn:hover:not(:disabled) {
         transform: translateY(-2px);
         box-shadow: 0 8px 24px rgba(255, 193, 7, 0.3);
       }

       .submit-btn:disabled {
         opacity: 0.7;
         cursor: not-allowed;
       }

       .btn-content {
         display: flex;
         align-items: center;
         justify-content: center;
         gap: 0.5rem;
         position: relative;
         z-index: 2;
       }

       .btn-icon {
         font-size: 1rem;
       }

       .spinning {
         animation: spin 1s linear infinite;
       }

       @keyframes spin {
         0% { transform: rotate(0deg); }
         100% { transform: rotate(360deg); }
       }

       .btn-shine {
         position: absolute;
         top: 0;
         left: -100%;
         width: 100%;
         height: 100%;
         background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
         animation: btn-shine 3s infinite;
       }

       @keyframes btn-shine {
         0% { left: -100%; }
         20% { left: 100%; }
         100% { left: 100%; }
       }

       .loading-state {
         display: flex;
         flex-direction: column;
         align-items: center;
         gap: 1rem;
         padding: 3rem;
         color: var(--text-muted);
       }

       .loading-spinner {
         font-size: 2rem;
         animation: spin 1s linear infinite;
       }

       .feeds-grid {
         display: grid;
         grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
         gap: 1.5rem;
       }

       .feed-card {
         background: var(--card-bg-lighter);
         border-radius: 12px;
         border: 1px solid var(--border-color);
         overflow: hidden;
         transition: transform 0.3s ease;
       }

       .feed-card:hover {
         transform: translateY(-4px);
       }

       .feed-card-header {
         display: flex;
         justify-content: space-between;
         align-items: center;
         padding: 1rem;
         border-bottom: 1px solid var(--border-color);
       }

       .feed-type-badge {
         display: flex;
         align-items: center;
         gap: 0.5rem;
         color: white;
         padding: 0.5rem 1rem;
         border-radius: 20px;
         font-size: 0.875rem;
         font-weight: 500;
       }

       .feed-actions {
         display: flex;
         gap: 0.5rem;
       }

       .status-toggle, .delete-btn {
         width: 32px;
         height: 32px;
         border: none;
         border-radius: 6px;
         display: flex;
         align-items: center;
         justify-content: center;
         cursor: pointer;
         transition: all 0.2s ease;
       }

       .status-toggle.active {
         background: var(--success);
         color: white;
       }

       .status-toggle.inactive {
         background: var(--text-muted);
         color: white;
       }

       .delete-btn {
         background: var(--danger);
         color: white;
       }

       .delete-btn:hover {
         background: #d32f2f;
         transform: scale(1.1);
       }

       .feed-image-container {
         width: 100%;
         height: 200px;
         overflow: hidden;
       }

       .feed-image {
         width: 100%;
         height: 100%;
         object-fit: cover;
       }

       .feed-content {
         padding: 1rem;
       }

       .feed-description {
         color: var(--text-color);
         font-size: 0.875rem;
         line-height: 1.5;
         margin: 0 0 1rem 0;
       }

       .feed-meta {
         display: flex;
         justify-content: space-between;
         align-items: center;
         font-size: 0.75rem;
       }

       .feed-date {
         display: flex;
         align-items: center;
         gap: 0.25rem;
         color: var(--text-muted);
       }

       .clock-icon {
         font-size: 0.75rem;
       }

       .feed-status.active {
         color: var(--success);
         font-weight: 600;
       }

       .feed-status.inactive {
         color: var(--text-muted);
         font-weight: 600;
       }

       .empty-state {
         display: flex;
         flex-direction: column;
         align-items: center;
         gap: 1rem;
         padding: 3rem;
         text-align: center;
       }

       .empty-icon {
         font-size: 3rem;
         color: var(--text-muted);
       }

       .empty-state h3 {
         color: var(--text-color);
         margin: 0;
       }

       .empty-state p {
         color: var(--text-muted);
         margin: 0;
       }

       /* Responsive Design */
       @media (max-width: 768px) {
         .admin-feed-container {
           padding: 10px;
         }

         .admin-feed-header {
           padding: 1.5rem 1rem;
         }

         .header-title-section {
           flex-direction: column;
           text-align: center;
           gap: 1rem;
         }

         .title-icon-wrapper {
           width: 56px;
           height: 56px;
         }

         .title-icon {
           font-size: 1.75rem;
         }

         .title-text h1 {
           font-size: 1.5rem;
         }

         .create-feed-section, .feeds-list-section {
           padding: 1.5rem;
         }

         .form-grid {
           grid-template-columns: 1fr;
         }

         .feed-type-grid {
           grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
         }

         .feeds-grid {
           grid-template-columns: 1fr;
         }
       }

       @media (max-width: 480px) {
         .feed-type-grid {
           grid-template-columns: 1fr;
         }

         .submit-btn {
           width: 100%;
         }
       }
     `}</style>
   </div>
 );
};

export default AdminAddFeed;